import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// ProvaSal di components/verifica/ProvaSal.js (passo 4 della scheda di verifica): lo stato che React teneva nel
// componente.
// - Chiusa all'inizio, nessuna richiesta. Aperta, il frame carica le copie demo (solo quelle con demo: true) e le
//   fonti salvate (VerificaController#prova_sal_copie); ogni apertura le ricarica, «Ricarica copie demo» anche.
// - Ogni apertura o chiusura e ogni cambio di copia azzerano la conferma, l'esito, l'errore e i prospetti
//   (useEffect su [project, grantId, open]); la conferma tolta o messa azzera l'esito; ogni modifica di un prospetto
//   azzera esito ed errore e chiede di nuovo la conferma.
// - «Esegui simulazioni SAL» si attiva con copia e conferma e chiama POST /app/verifica/{id}/collaudo-sal
//   (VerificaController#collaudo_sal) con il corpo di ProvaSal.js: {project_id, conferma_simulazione: true} più i
//   prospetti inclusi (prospetto_personale, prospetto_vincolato, prospetti_documentali: solo i campi toccati, come lo
//   stato di React; «Da verificare» è null, Sì/No sono true/false, il resto testo). L'esito è il turbo-stream che
//   riscrive «prova-sal-esito-{id}»; un errore è il JSON {detail} nell'avviso. Ogni modifica incrementa `richiesta`:
//   una risposta di una richiesta superata non si mostra («il risultato tardivo di una copia precedente»).
export default class extends Controller {
  static targets = ["apri", "pannello", "copia", "frame", "caricamento", "nessuna", "conferma", "esegui", "errore", "erroreTesto", "esito"]
  static values = { url: String, eseguiUrl: String }

  connect() {
    this.aperto = false
    this.copie = null
    this.errore = ""
    this.richiesta = 0
    this.occupato = false
  }

  apri() {
    this.aperto = !this.aperto
    this.apriTarget.textContent = this.aperto ? "Chiudi prove su copia demo" : "Prova SAL e pagamenti su copia demo"
    this.pannelloTarget.hidden = !this.aperto
    if (this.aperto) this.carica()
    this.azzera()
  }

  carica() {
    this.copie = null
    this.errore = ""
    const frame = this.frameTarget
    if (frame.getAttribute("src")) frame.reload()
    else frame.src = this.urlValue
    this.mostra()
  }

  ricarica() {
    this.carica()
  }

  // turbo:frame-load: le copie e le fonti, o il dettaglio dell'errore
  caricate(event) {
    if (event.target !== this.frameTarget || !this.aperto) return
    const dati = this.frameTarget.querySelector("[data-copie]")
    if (!dati) { this.nonCaricate(); return }
    if (dati.dataset.errore !== undefined) { this.errore = dati.dataset.errore || "Copie demo non caricate."; this.mostra(); return }
    this.copie = JSON.parse(dati.dataset.copie)
    this.scriviCopie()
    this.scriviFonti(JSON.parse(dati.dataset.fonti || "[]"))
    this.mostra()
  }

  mancanti(event) {
    if (event.target !== this.frameTarget) return
    event.preventDefault()
    this.nonCaricate()
  }

  nonCaricate(event) {
    if (event && !this.frameTarget.contains(event.target) && event.target !== this.frameTarget) return
    if (!this.aperto) return
    this.errore = "Copie demo non caricate."
    this.mostra()
  }

  scriviCopie() {
    const select = this.copiaTarget
    const scelta = select.value
    select.querySelectorAll("option:not([value=''])").forEach((o) => o.remove())
    this.copie.forEach((p) => {
      const o = document.createElement("option")
      o.value = p.id
      o.textContent = `${p.name} · ${p.created_at ? new Date(p.created_at).toLocaleString("it-IT") : p.id}`
      select.append(o)
    })
    select.value = this.copie.some((p) => p.id === scelta) ? scelta : ""
  }

  scriviFonti(fonti) {
    this.element.querySelectorAll("[data-prova-sal-fonti]").forEach((select) => {
      const scelta = select.value
      select.querySelectorAll("option:not([value=''])").forEach((o) => o.remove())
      fonti.forEach((f) => {
        const o = document.createElement("option")
        o.value = f.id
        o.textContent = `${f.punto} — ${f.descrizione}`
        select.append(o)
      })
      select.value = fonti.some((f) => f.id === scelta) ? scelta : ""
    })
  }

  mostra() {
    this.caricamentoTarget.hidden = !(this.copie === null && !this.errore)
    this.nessunaTarget.hidden = !(this.copie && this.copie.length === 0)
    this.erroreTarget.hidden = !this.errore
    this.erroreTestoTarget.textContent = this.errore
    this.eseguiTarget.disabled = this.occupato || !(this.copiaTarget.value && this.confermaTarget.checked)
    this.eseguiTarget.textContent = this.occupato ? "Simulazione in corso…" : "Esegui simulazioni SAL"
  }

  // run(): la richiesta di ProvaSal.js
  async esegui() {
    if (!this.copiaTarget.value || !this.confermaTarget.checked || this.occupato) return
    const n = ++this.richiesta
    this.occupato = true
    this.esitoTarget.innerHTML = ""
    this.errore = ""
    this.mostra()
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const risposta = await fetch(this.eseguiUrlValue, {
        method: "POST", credentials: "same-origin",
        headers: { Accept: "text/vnd.turbo-stream.html", "Content-Type": "application/json", ...(token ? { "X-CSRF-Token": token } : {}) },
        body: JSON.stringify(this.corpo()),
      })
      const tipo = risposta.headers.get("Content-Type") || ""
      if (risposta.ok && tipo.includes("turbo-stream")) {
        const testo = await risposta.text()
        if (n === this.richiesta) Turbo.renderStreamMessage(testo)
      } else {
        const dati = tipo.includes("json") ? await risposta.json().catch(() => null) : null
        if (n === this.richiesta) this.errore = (typeof dati?.detail === "string" && dati.detail) || "Simulazione non eseguita."
      }
    } catch {
      if (n === this.richiesta) this.errore = "Simulazione non eseguita."
    } finally {
      if (n === this.richiesta) { this.occupato = false; this.mostra() }
    }
  }

  corpo() {
    const corpo = { project_id: this.copiaTarget.value, conferma_simulazione: true }
    const personale = this.prospetto("prospetto_personale")
    if (personale !== null) corpo.prospetto_personale = personale
    const vincolato = this.prospetto("prospetto_vincolato")
    if (vincolato !== null) corpo.prospetto_vincolato = vincolato
    const documentali = this.documentali()
    if (documentali !== null) corpo.prospetti_documentali = documentali
    return corpo
  }

  incluso(chiave) {
    return this.element.querySelector(`[data-prova-sal-includi="${chiave}"]`)?.checked === true
  }

  // {} con i soli campi toccati di `radice` (fuori dalle righe aggiunte), null se «Includi» non è spuntato
  prospetto(chiave) {
    if (!this.incluso(chiave)) return null
    return this.campi(this.element.querySelector(`[data-prova-sal-campi="${chiave}"]`))
  }

  documentali() {
    if (!this.incluso("prospetti_documentali")) return null
    const out = {}
    this.element.querySelectorAll("[data-prova-sal-tipo]").forEach((tipo) => {
      const kind = tipo.dataset.provaSalTipo
      if (!tipo.querySelector(`[data-prova-sal-includi-tipo="${kind}"]`)?.checked) return
      const dati = this.campi(tipo.querySelector(`[data-prova-sal-campi-tipo="${kind}"]`))
      tipo.querySelectorAll("[data-prova-sal-righe]").forEach((righe) => {
        const elenco = [...righe.querySelectorAll(":scope > [data-prova-sal-riga]")].map((r) => this.campi(r))
        if (righe.dataset.usato) dati[righe.dataset.provaSalRighe] = elenco
      })
      out[kind] = dati
    })
    return out
  }

  campi(radice) {
    const dati = {}
    radice.querySelectorAll("[data-chiave]").forEach((el) => {
      if (el.dataset.toccato !== "1" || el.closest("[data-prova-sal-riga]") !== (radice.matches("[data-prova-sal-riga]") ? radice : null)) return
      dati[el.dataset.chiave] = el.dataset.tri !== undefined ? (el.value === "" ? null : el.value === "true") : el.value
    })
    return dati
  }

  // input/change dentro la prova: la copia azzera tutto, la conferma l'esito, un prospetto chiede una nuova conferma
  cambio(event) {
    const el = event.target
    if (el.dataset?.chiave) el.dataset.toccato = "1"
    if (el === this.copiaTarget) {
      if (event.type === "change") this.azzera()
    } else if (el === this.confermaTarget) {
      this.supera()
      this.esitoTarget.innerHTML = ""
    } else if (el.closest?.("[data-prova-sal-prospetto]")) {
      this.supera()
      this.includi(el)
      this.confermaTarget.checked = false
      this.esitoTarget.innerHTML = ""
      this.errore = ""
    }
    this.mostra()
  }

  // «Includi»: da null a {} (campi vuoti) e indietro
  includi(el) {
    const chiave = el.dataset.provaSalIncludi
    if (chiave) this.svuota(this.element.querySelector(`[data-prova-sal-campi="${chiave}"]`), el.checked)
    const tipo = el.dataset.provaSalIncludiTipo
    if (tipo) this.svuota(this.element.querySelector(`[data-prova-sal-campi-tipo="${tipo}"]`), el.checked)
  }

  svuota(campi, visibili) {
    if (!campi) return
    campi.querySelectorAll("[data-toccato]").forEach((c) => { delete c.dataset.toccato })
    campi.querySelectorAll("input[type='checkbox']").forEach((c) => { c.checked = false })
    campi.querySelectorAll("input:not([type='checkbox']), select").forEach((c) => { c.value = "" })
    campi.querySelectorAll("[data-prova-sal-campi-tipo]").forEach((c) => { c.hidden = true })
    campi.querySelectorAll("[data-prova-sal-righe]").forEach((c) => { c.innerHTML = ""; delete c.dataset.usato })
    campi.querySelectorAll("[data-prova-sal-aggiungi]").forEach((b) => { b.disabled = false })
    campi.hidden = !visibili
  }

  // request.current += 1; setBusy(false): la risposta di una richiesta in volo non si mostra più
  supera() {
    this.richiesta += 1
    this.occupato = false
  }

  // useEffect su [project, grantId, open]: esito, conferma, errore e prospetti azzerati
  azzera() {
    this.supera()
    this.confermaTarget.checked = false
    this.esitoTarget.innerHTML = ""
    if (this.copie !== null) this.errore = ""
    this.element.querySelectorAll("[data-prova-sal-includi]").forEach((c) => {
      c.checked = false
      this.svuota(this.element.querySelector(`[data-prova-sal-campi="${c.dataset.provaSalIncludi}"]`), false)
    })
    this.mostra()
  }

  aggiungiRiga(event) {
    const pulsante = event.currentTarget
    const chiave = pulsante.dataset.provaSalAggiungi
    const tipo = pulsante.closest("[data-prova-sal-tipo]")
    const righe = tipo.querySelector(`[data-prova-sal-righe="${chiave}"]`)
    righe.dataset.usato = "1"
    righe.insertAdjacentHTML("beforeend", tipo.querySelector(`[data-prova-sal-modello="${chiave}"]`).innerHTML)
    this.rinumera(righe, pulsante)
    this.cambio({ target: pulsante, type: "change" })
  }

  togliRiga(event) {
    const riga = event.currentTarget.closest("[data-prova-sal-riga]")
    const righe = riga.parentElement
    const tipo = righe.closest("[data-prova-sal-tipo]")
    riga.remove()
    this.rinumera(righe, tipo.querySelector(`[data-prova-sal-aggiungi="${righe.dataset.provaSalRighe}"]`))
    this.cambio({ target: tipo, type: "change" })
  }

  rinumera(righe, pulsante) {
    const tutte = [...righe.querySelectorAll(":scope > [data-prova-sal-riga]")]
    tutte.forEach((r, i) => r.querySelectorAll("[data-numero]").forEach((n) => { n.textContent = String(i + 1) }))
    if (pulsante) pulsante.disabled = tutte.length >= Number(pulsante.dataset.massimo)
  }
}
